import { useWrapper } from "@context/wrapper";
import React, { Children, useEffect, useRef, useState } from "react";
import { FiChevronLeft } from "react-icons/fi";

export interface PaginationProps {
  itemsPerPage: number;
  childWrapperClassName?: string;
}

const Pagination: React.FC<React.PropsWithChildren<PaginationProps>> = ({
  itemsPerPage,
  childWrapperClassName,
  children,
}) => {
  const { wrapperRef } = useWrapper();

  const childArray = Children.toArray(children);
  const totalItems = childArray?.length ?? 0;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const isDotsRendered = useRef(false);
  const isNextDotsRendered = useRef(false);
  const start = (currentPage - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  const slicedChildren = childArray?.slice(start, end);

  useEffect(() => {
    setCurrentPage(1);
  }, [children]);

  const handleScrollToTop = () => {
    if (wrapperRef && wrapperRef.current) {
      wrapperRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePageChange = (pageChange: "prev" | "next") => {
    isDotsRendered.current = false;
    isNextDotsRendered.current = false;
    if (pageChange === "prev" && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      handleScrollToTop();
      return;
    }
    if (pageChange === "next" && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      handleScrollToTop();
      return;
    }
  };

  const renderPrevNext = (type: "prev" | "next") => {
    return (
      <div
        className="cursor-pointer text-sm text-[#656565] flex gap-1 items-center select-none mx-1"
        onClick={() => handlePageChange(type)}
      >
        {type === "prev" && <FiChevronLeft />}
        {type === "prev" ? "Previous" : "Next"}
        {type === "next" && <FiChevronLeft className="rotate-180" />}
      </div>
    );
  };

  const renderPaginationNumberAndArrows = () => {
    if (totalPages <= 1) return <></>;
    return (
      <div className="flex gap-2 items-center justify-end mt-8">
        {renderPrevNext("prev")}
        <div className="flex gap-2 justify-center">
          {Array.from({ length: totalPages }, (_, i) => {
            const page = i + 1;
            const activePage = currentPage === page;
            if (
              i <= 1 ||
              i >= totalPages - 2 ||
              activePage ||
              (currentPage - 1 <= i && i <= currentPage + 1)
            ) {
              return (
                <div
                  key={i}
                  className={`transition-all shrink-0 px-4 py-2 rounded-[6px] bg-transparent cursor-pointer outline-transparent outline hover:outline-[#ededed] hover:bg-white ${
                    activePage
                      ? "bg-primary-blue-40 outline-primary-blue-40 hover:bg-primary-blue-40 hover:outline-primary-blue-40 text-primary-blue-40"
                      : ""
                  }`}
                  onClick={() => {
                    isDotsRendered.current = false;
                    isNextDotsRendered.current = false;
                    handleScrollToTop();
                    setCurrentPage(page);
                  }}
                >
                  {page}
                </div>
              );
            } else {
              if (i >= 2 && i < currentPage && !isDotsRendered.current) {
                isDotsRendered.current = true;
                return (
                  <div className="shrink-0 flex items-center" key={`One${i}`}>
                    ...
                  </div>
                );
              }
              if (i >= currentPage + 2 && !isNextDotsRendered.current) {
                isNextDotsRendered.current = true;
                return (
                  <div className="shrink-0 flex items-center" key={`Two${i}`}>
                    ...
                  </div>
                );
              }
            }
          })}
        </div>
        {renderPrevNext("next")}
      </div>
    );
  };

  return (
    <>
      <div className={childWrapperClassName}>{slicedChildren}</div>
      {renderPaginationNumberAndArrows()}
    </>
  );
};

export default Pagination;
